import { useEffect, useState, useRef, useCallback } from 'react'
import styles from './restosList.module.scss'
import { MarkerData } from 'Types/markers'
import styled, { useTheme } from 'styled-components'
import { device } from '../breakpoints'
import { Button } from '@mui/material'
import locales from 'locales/locales'

declare global {
  interface Window {
    dataLayer: any
  }
}

function getDistanceFromLatLonInKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) {
  var R = 6371 // Radius of the earth in km
  var dLat = deg2rad(lat2 - lat1) // deg2rad below
  var dLon = deg2rad(lon2 - lon1)
  var a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  var d = R * c // Distance in km
  return d
}

function deg2rad(deg: number) {
  return (deg * Math.PI) / 180
}

function getDistanceStr(meters: number) {
  if (meters < 1000) {
    return (Math.round(meters / 10) * 10).toString() + 'm'
  } else if (meters < 4000) {
    return (Math.round(meters / 100) / 10).toString() + 'km'
  } else {
    return Math.round(meters / 1000).toString() + 'km'
  }
}

export const RestosList = ({
  restos,
  publisher,
  handleClick,
  userLocation,
}: {
  restos: MarkerData[]
  publisher: string
  handleClick: (marker: MarkerData) => void
  userLocation: { lat: number; lng: number } | null | undefined
}) => {
  const countStep = 12
  const [showCount, setShowCount] = useState(countStep)
  const [sortedRestos, setSortedRestos] = useState<MarkerData[]>([])
  const loader = useRef(null)
  const theme = useTheme()

  // If there is a new list of restos or a new user location, add the distance to the user and sort them
  useEffect(() => {
    if (userLocation) {
      const list = restos.map((resto) => ({
        ...resto,
        distance: getDistanceFromLatLonInKm(
          userLocation.lat,
          userLocation.lng,
          resto.latitude,
          resto.longitude
        ),
      }))

      setSortedRestos(list.sort((a, b) => a.distance - b.distance))
    } else {
      const center =
        publisher === 'tdg'
          ? { lat: 46.2044, lng: 6.1432 }
          : { lat: 46.5197, lng: 6.6323 }
      const list = restos.map((resto) => ({
        ...resto,
        distanceFromCenter: getDistanceFromLatLonInKm(
          center.lat,
          center.lng,
          resto.latitude,
          resto.longitude
        ),
      }))

      setSortedRestos(
        list.sort((a, b) => a.distanceFromCenter - b.distanceFromCenter)
      )
    }
  }, [restos, userLocation, publisher])

  return (
    <Container>
      <ListContent>
        {sortedRestos.slice(0, showCount).map((resto) => (
          <ItemContainer
            publisher={publisher}
            className={resto.main_style}
            key={`resto-${resto.markerId}`}
            onClick={() => {
              handleClick(resto)

              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `showfestival:${resto.markerId}`,
              })
            }}
          >
            <ItemContent>
              <div>
                <ItemTitle>{resto.name}</ItemTitle>
                <ItemDates>{resto?.date_str}</ItemDates>
                <ItemLocation>{resto?.address}</ItemLocation>
                <ItemStyles>
                  {resto?.style_list &&
                    resto?.style_list.map((text, index) => (
                      <InlineFilter key={`filter_${index}`}>
                        {text}
                      </InlineFilter>
                    ))}
                </ItemStyles>

                {resto?.distance && (
                  <ItemDistance>
                    {getDistanceStr(resto?.distance * 1000)}
                  </ItemDistance>
                )}
              </div>
            </ItemContent>
          </ItemContainer>
        ))}
      </ListContent>
      <div
        ref={loader}
        style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}
      >
        {sortedRestos.length > showCount && (
          <Button
            variant='contained'
            disableElevation
            onClick={() => {
              setShowCount(showCount + countStep)

              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'Interactions',
                event_action: 'click',
                event_label: `showmore`,
              })
            }}
          >
            {locales.buttonSeeMore[theme.lang]}
          </Button>
        )}
      </div>
    </Container>
  )
}

const ItemContent = styled.div`
  width: 100%;
  background: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  overflow: hidden;
  // display: grid;
  // grid-template-columns: 2fr 1fr;

  @media ${device.mobileS}, ${device.mobileM}, ${device.mobileL} {
    padding-top: 0.65em;
  }

  @media ${device.tablet}, ${device.laptop} {
    padding-top: 0.65em;
  }
`

const ItemTitle = styled.div`
  font-family: var(--font-sans-serif);
  font-weight: 600;
  font-size: 1em;
  line-height: 1.2em;
  margin-bottom: 0.15em;
`

const ItemDistance = styled.div`
  font-size: 0.8em;
  color: #a2a3a5;
`
const ItemDates = styled.div`
  font-family: var(--font-sans-serif);
  overflow: hidden;
  position: relative;
  font-size: 0.865em;
  line-height: 1.2em;
  opacity: 0.8;
`

const ItemLocation = styled.div`
  font-family: var(--font-sans-serif);
  overflow: hidden;
  width: calc(90%);
  opacity: 0.8;
  position: relative;
  font-size: 0.865em;
  line-height: 1.2em;
  text-overflow: ellipsis;
  display: block;
  white-space: nowrap;
  margin-top: 1rem;
  margin-bottom: 1rem;
`
const ItemStyles = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`
const Container = styled.div`
  position: relative;
`
const ListContent = styled.div`
  position: relative;
  display: grid;
  padding: 1em;
  padding-top: 0;
  grid-auto-rows: minmax(min-content, max-content);

  @media ${device.mobileS}, ${device.mobileM}, ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-gap: 30px;
  }

  @media ${device.tablet}, ${device.laptop} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }

  @media ${device.laptopL} {
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 20px;
  }
`

const ItemContainer = styled.div<{ publisher?: string }>`
  display: grid;
  position: relative;
  cursor: pointer;
  width: 100%;
  overflow: hidden;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.1);
  -moz-border-radius: 2px 15px 15px 2px;
  border-radius: 2px 15px 15px 2px;
  border-left: 1rem solid var(--tenant-24h);
  border-color: ${(props) =>
    props.publisher === '24heures' ? 'var(--tenant-24h)' : 'var(--tenant-tdg)'};

  // border-left: 1rem solid #e96dd6;
  // &.pop {
  //   border-color: #fb5b5b;
  // }
  // &.electro {
  //   border-color: #fb5b5b;
  // }
  // &.classique {
  //   border-color: #1f4fe0;
  // }
  // &.jazz {
  //   border-color: #e0ab1f;
  // }
  // &.pluridisciplinaire {
  //   border-color: #e96dd6;
  // }
  padding-left: 1rem;

  @media ${device.mobileS}, ${device.mobileM}, ${device.mobileL} {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }

  @media ${device.tablet}, ${device.laptop} {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(min-content, max-content) 1fr;
  }
`

const InlineFilter = styled.div`
  display: inline-block;
  padding: 0.25em 1rem;
  background: ${(props) => props.theme.greyColor};
  color: ${(props) => props.theme.textColor};
  border-radius: 5px;
  margin-right: 1rem;
  margin-bottom: 1rem;
  font-size: 1.5rem;

  &:last-child {
    margin-right: 0;
  }
`
